/* eslint-disable react/require-default-props */
/* eslint-disable no-nested-ternary */
import React, { useLayoutEffect, useReducer, useMemo, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectTable, setCurrentPaginationNumber, TableNameOptions } from '@landscape/ui-redux'

import { TableProvider } from './TableContext'
import { tableReducer } from './TableReducer'
import { TableHeadRow } from './TableHeadRow'
import { TableHeader, HeaderActionProps } from './TableHeader'
import { TableRow } from './TableRow'
import { Pagination } from './Pagination'
import useDidUpdateEffect from './useDidUpdateEffect'
import { getSortDirection, sort, multiSort, getNumberOfPages, recalculatePage } from './utils'
import column, { IState } from './interface'

interface TableProps {
  data: {
    [key: string]: number | string | boolean
  }[]
  id?: string
  columns: column[]
  uniqueKeyColumn: string
  condensed?: boolean
  title?: string
  titlePosition?: string
  defaultSortField?: string
  actions?: React.FC<HeaderActionProps>[]
  sortFn?: Function
  onSort?: Function
  defaultSortAsc?: boolean
  enableMultiSort?: boolean
  paginationDefaultPage?: number
  paginationRowPerPage?: number
  pagination?: boolean
  paginationRowsPerPageOptions?: number[]
  reduxTableName?: TableNameOptions
  accessibilityMode?: boolean
}

export const Table: React.FC<TableProps> = ({
  id,
  data,
  columns,
  condensed = false,
  title,
  titlePosition = 'center',
  actions,
  onSort,
  sortFn,
  defaultSortField,
  defaultSortAsc = true,
  enableMultiSort = false,
  paginationDefaultPage = 1,
  paginationRowPerPage = 5,
  pagination,
  paginationRowsPerPageOptions = [5, 10, 15],
  uniqueKeyColumn,
  reduxTableName,
  accessibilityMode = false,
}) => {
  const initialState: IState = {
    rowsPerPage: paginationRowPerPage || 0,
    defaultSortField,
    defaultSortAsc,
    sortColumn: defaultSortField,
    sortDirection: getSortDirection(defaultSortAsc),
    sortMultiColumns: enableMultiSort ? (defaultSortField ? [defaultSortField] : []) : undefined,
    sortMultiDirections: enableMultiSort ? [getSortDirection(defaultSortAsc)] : undefined,
    selectedColumn: undefined,
  }

  const reduxDispatch = useDispatch()
  const reduxTableState = useSelector(selectTable)

  let currentPage
  if (pagination) {
    currentPage = reduxTableState[reduxTableName].currentPaginationNumber
  }

  const [{ sortColumn, selectedColumn, sortDirection, sortMultiColumns, sortMultiDirections, rowsPerPage }, dispatch] =
    useReducer(tableReducer, initialState)

  useDidUpdateEffect(() => {
    onSort && onSort(selectedColumn, sortDirection)
  }, [sortColumn, sortDirection])

  const handleChangePage = (page: number) => {
    reduxDispatch(
      setCurrentPaginationNumber({
        reduxTableName,
        data: {
          currentPaginationNumber: page,
        },
      })
    )
  }

  useDidUpdateEffect(() => {
    handleChangePage(paginationDefaultPage)
  }, [paginationDefaultPage])

  const memoizedColumns = useMemo(() => columns, [columns])

  const sortedData = useMemo(() => {
    if (enableMultiSort) {
      return multiSort(data, sortMultiColumns, sortMultiDirections!, defaultSortField, getSortDirection(defaultSortAsc))
    }
    return sort(data, sortColumn, sortDirection, defaultSortField, getSortDirection(defaultSortAsc), sortFn)
  }, [
    data,
    sortColumn,
    sortDirection,
    sortFn,
    enableMultiSort,
    sortMultiColumns,
    sortMultiDirections,
    defaultSortField,
    defaultSortAsc,
  ])

  const calculatedRows = useMemo(() => {
    if (pagination) {
      // when using client-side pagination we can just slice the data set
      const currentIndex = currentPage * rowsPerPage
      const previousIndex = currentIndex - rowsPerPage

      return sortedData.slice(previousIndex, currentIndex)
    }
    return sortedData
  }, [currentPage, pagination, rowsPerPage, sortedData])

  if (pagination && data.length > 0 && calculatedRows.length === 0) {
    const updatedPage = getNumberOfPages(data.length, rowsPerPage)
    const recalculatedPage = recalculatePage(currentPage, updatedPage)
    handleChangePage(recalculatedPage)
  }

  // TODO needs to be updated to handle currentPage being
  // managed by Redux.
  // const handleChangeRowsPerPage = (newRowsPerPage: number) => {
  //   const rowCount = calculatedRows.length
  //   const updatedPage = getNumberOfPages(rowCount, newRowsPerPage)
  //   const recalculatedPage = recalculatePage(currentPage, updatedPage)
  //   handleChangePage(recalculatedPage)
  //   dispatch({ type: 'CHANGE_ROWS_PER_PAGE', payload: { rowsPerPage: newRowsPerPage } })
  // }

  const init = {
    dispatch,
    data,
    sortColumn,
    sortDirection,
    sortMultiColumns,
    sortMultiDirections,
    selectedColumn,
    pagination,
    paginationRowsPerPageOptions,
    enableMultiSort,
    defaultSortAsc,
    uniqueKeyColumn,
  }
  return (
    <div className="tableComponent">
      <TableProvider initialState={init}>
        <TableHeader actions={actions} />
        <table id={id} className={`cb-table cb-margin-bottom-16 ${condensed && 'cb-table-condensed'}`}>
          {title && (
            <caption>
              <h6 className={`cb-h6 display-flex justify-content-${titlePosition}`}>{title}</h6>
            </caption>
          )}
          <TableHeadRow columns={memoizedColumns} />
          <tbody>
            {calculatedRows.map((row: { [key: string]: number | string | boolean }) => (
              <TableRow
                key={row[uniqueKeyColumn] as string}
                row={row}
                columns={memoizedColumns}
                accessibilityMode={accessibilityMode}
              />
            ))}
          </tbody>
        </table>
        {pagination && (
          <Pagination
            reduxTableName={reduxTableName}
            onChangePage={handleChangePage}
            // onChangeRowsPerPage={handleChangeRowsPerPage}
            rowCount={data.length}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
          />
        )}
      </TableProvider>
    </div>
  )
}
